import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { User, CheckCircle, ArrowRight, BookOpen, AlertCircle, Clock, Users, Calculator, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CALENDLY_URL = 'https://calendly.com/ismail-haamud98/30min';

const FALLBACK_COURSES = [
  { id: 'noorani-qaida', name: 'Quran Online Method', level: 'beginner', price_per_lesson: 125, old_group_price: 145, private_enabled: true, private_price_min: 200, private_price_max: 220, description: 'Grundläggande arabisk läsning och uttal' },
  { id: 'tajweed', name: 'Tajweed', level: 'intermediate', price_per_lesson: 135, old_group_price: 150, private_enabled: true, private_price_min: 220, private_price_max: 250, description: 'Korrekt recitation med tajweed-regler' },
  { id: 'hifdh', name: 'Hifdh', level: 'advanced', price_per_lesson: 150, old_group_price: 180, private_enabled: true, private_price_min: 250, private_price_max: 300, description: 'Koranmemorering med erfaren lärare' },
];

const BookingPage = () => {
  const { language } = useLanguage();
  const sv = language === 'sv';
  const [courses, setCourses] = useState(FALLBACK_COURSES);
  const [regStatus, setRegStatus] = useState({ registration_open: true, student_limit: 30, current_students: 0 });
  const [form, setForm] = useState({ name: '', age: '', email: '', phone: '', preferred_course: '', class_type: 'group', message: '' });
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lessonsPerWeek, setLessonsPerWeek] = useState(2);

  useEffect(() => {
    fetch(`${API_URL}/api/public/courses`).then(r => r.json()).then(d => { if (d?.length) setCourses(d); }).catch(() => {});
    fetch(`${API_URL}/api/public/registration-status`).then(r => r.json()).then(d => { if (d?.registration_open !== undefined) setRegStatus(d); }).catch(() => {});
  }, []);

  const selectedCourse = courses.find(c => c.name === form.preferred_course);
  const priceMin = form.class_type === 'private' && selectedCourse?.private_enabled ? selectedCourse.private_price_min : selectedCourse?.price_per_lesson || 0;
  const priceMax = form.class_type === 'private' && selectedCourse?.private_enabled ? selectedCourse.private_price_max : selectedCourse?.price_per_lesson || 0;
  const isRange = priceMin !== priceMax;
  const monthlyCostMin = priceMin * lessonsPerWeek * 4;
  const monthlyCostMax = priceMax * lessonsPerWeek * 4;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/public/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) setSubmitted(data);
      else setError(data.detail || (sv ? 'Något gick fel' : 'Something went wrong'));
    } catch {
      // Backend unavailable — show helpful message instead of WhatsApp redirect
      setSubmitted({
        status: 'offline',
        message: sv
          ? 'Formuläret kunde inte skickas just nu. Boka istället din provlektion via Calendly-knappen ovan, eller kontakta oss på connect@quranonline.se'
          : 'The form could not be submitted right now. Please book your trial lesson via the Calendly button above, or contact us at connect@quranonline.se'
      });
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="booking-page">
      <section className="py-16 islamic-pattern hero-gradient">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm text-gold mb-6" style={{ border: '1px solid rgba(206, 150, 8, 0.3)' }}>
            <BookOpen size={16} /> {sv ? 'Ansök om plats' : 'Apply for a spot'}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Ansökan' : 'Application'}
          </h1>
          <p style={{ color: 'var(--text-secondary)' }} className="max-w-2xl mx-auto">
            {sv ? 'Fyll i formuläret nedan. Vi kontaktar dig och placerar dig i en lämplig grupp.' : 'Fill in the form below. We will contact you and place you in a suitable group.'}
          </p>
        </div>
      </section>
      <div className="gold-divider"></div>

      {!regStatus.registration_open && (
        <div className="max-w-3xl mx-auto px-4 mt-8">
          <div className="flex items-center gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30" data-testid="registration-closed-banner">
            <AlertCircle size={20} className="text-yellow-400 flex-shrink-0" />
            <p className="text-sm text-yellow-400">
              {sv ? 'Registreringen är stängd. Du kan fortfarande ansöka och bli placerad på väntelistan.' : 'Registration is closed. You can still apply and be placed on the waiting list.'}
            </p>
          </div>
        </div>
      )}

      <section className="py-12" data-testid="booking-content">
        <div className="max-w-3xl mx-auto px-4">

          {/* Calendly Booking */}
          <div className="card-dark p-8 md:p-12 mb-8 text-center" data-testid="calendly-section">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ border: '2px solid var(--gold)' }}>
              <Calendar size={28} className="text-gold" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
              {sv ? 'Boka gratis provlektion' : 'Book Free Trial Lesson'}
            </h2>
            <p className="mb-6 max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
              {sv ? 'Välj en tid som passar dig. Provlektionen är helt kostnadsfri och utan förpliktelse.' : 'Choose a time that suits you. The trial lesson is completely free with no obligation.'}
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
              className="btn-gold inline-flex items-center justify-center gap-2 text-lg py-4 px-10" data-testid="calendly-book-btn">
              {sv ? 'Välj tid via Calendly' : 'Choose time via Calendly'} <ArrowRight size={20} />
            </a>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              {[sv ? 'Gratis provlektion' : 'Free trial', sv ? 'Ingen förpliktelse' : 'No obligation', sv ? '30 minuter' : '30 minutes'].map((b, i) => (
                <span key={i} className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle size={14} className="text-gold" /> {b}
                </span>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-color)' }}></div>
            <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
              {sv ? 'Eller skicka en ansökan' : 'Or submit an application'}
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-color)' }}></div>
          </div>

          <div className="card-dark p-8 md:p-12">
            {submitted ? (
              <div className="text-center py-12" data-testid="booking-success">
                <div className="w-20 h-20 border-2 border-gold rounded-full flex items-center justify-center mx-auto mb-6">
                  {submitted.status === 'waiting_list' ? <Clock size={40} className="text-gold" /> : <CheckCircle size={40} className="text-gold" />}
                </div>
                <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                  {submitted.status === 'waiting_list' ? (sv ? 'Du är på väntelistan!' : "You're on the waiting list!") : (sv ? 'Tack för din ansökan!' : 'Thank you!')}
                </h2>
                <p style={{ color: 'var(--text-secondary)' }}>{submitted.message}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="booking-form">
                {/* Personal Info */}
                <div className="mb-10">
                  <h3 className="text-lg font-semibold mb-6 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <User size={20} className="text-gold" /> {sv ? 'Dina uppgifter' : 'Your information'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Namn' : 'Name'} *</label>
                      <input type="text" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full h-12 px-4 rounded" data-testid="booking-name" />
                    </div>
                    <div>
                      <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>E-post *</label>
                      <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full h-12 px-4 rounded" data-testid="booking-email" />
                    </div>
                    <div>
                      <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Telefon' : 'Phone'}</label>
                      <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full h-12 px-4 rounded" data-testid="booking-phone" />
                    </div>
                    <div>
                      <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Ålder' : 'Age'}</label>
                      <input type="text" value={form.age} onChange={e => setForm(f => ({ ...f, age: e.target.value }))} className="w-full h-12 px-4 rounded" data-testid="booking-age" />
                    </div>
                  </div>
                </div>

                {/* Class Type */}
                <div className="mb-10">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <Users size={20} className="text-gold" /> {sv ? 'Lektionstyp' : 'Lesson Type'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className={`flex items-start gap-4 p-5 rounded cursor-pointer transition-all`}
                      style={{ border: form.class_type === 'group' ? '2px solid var(--gold)' : '1px solid var(--border-color)', backgroundColor: form.class_type === 'group' ? 'rgba(206, 150, 8, 0.05)' : 'transparent' }}>
                      <input type="radio" name="class_type" value="group" checked={form.class_type === 'group'} onChange={e => setForm(f => ({ ...f, class_type: e.target.value }))} className="mt-1" />
                      <div>
                        <p className="font-medium flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                          <Users size={16} className="text-gold" /> {sv ? 'Grupplektion' : 'Group Lesson'}
                          <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">{sv ? 'Rekommenderas' : 'Recommended'}</span>
                        </p>
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{sv ? '2-5 elever · Personlig uppmärksamhet' : '2-5 students · Personal attention'}</p>
                      </div>
                    </label>
                    <label className={`flex items-start gap-4 p-5 rounded cursor-pointer transition-all`}
                      style={{ border: form.class_type === 'private' ? '2px solid var(--gold)' : '1px solid var(--border-color)', backgroundColor: form.class_type === 'private' ? 'rgba(206, 150, 8, 0.05)' : 'transparent' }}>
                      <input type="radio" name="class_type" value="private" checked={form.class_type === 'private'} onChange={e => setForm(f => ({ ...f, class_type: e.target.value }))} className="mt-1" />
                      <div>
                        <p className="font-medium flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                          <User size={16} className="text-gold" /> {sv ? 'Privatlektion Premium (1-on-1)' : 'Private Premium Lesson (1-on-1)'}
                        </p>
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Individuell undervisning · Högre pris' : 'Individual teaching · Higher price'}</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Course Selection */}
                <div className="mb-10">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <BookOpen size={20} className="text-gold" /> {sv ? 'Välj kurs' : 'Select Course'}
                  </h3>
                  <div className="space-y-3">
                    {courses.map(c => {
                      const cpMin = form.class_type === 'private' && c.private_enabled ? c.private_price_min : c.price_per_lesson;
                      const cpMax = form.class_type === 'private' && c.private_enabled ? c.private_price_max : c.price_per_lesson;
                      return (
                        <label key={c.id} className="flex items-center gap-4 p-4 rounded cursor-pointer transition-all"
                          style={{ border: form.preferred_course === c.name ? '2px solid var(--gold)' : '1px solid var(--border-color)', backgroundColor: form.preferred_course === c.name ? 'rgba(206, 150, 8, 0.05)' : 'transparent' }}>
                          <input type="radio" name="course" value={c.name} checked={form.preferred_course === c.name} onChange={e => setForm(f => ({ ...f, preferred_course: e.target.value }))} className="w-4 h-4" />
                          <div className="flex-1">
                            <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{c.name}</span>
                            {c.description && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{c.description}</p>}
                          </div>
                          <span className="text-gold font-semibold text-sm">{cpMin !== cpMax ? <><span className="line-through text-xs mr-1" style={{ color: 'var(--text-muted)' }}>{cpMax}</span>{cpMin}</> : cpMin} SEK</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Monthly Calculator */}
                {selectedCourse && (
                  <div className="mb-10 p-5 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }} data-testid="booking-calculator">
                    <p className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <Calculator size={16} className="text-gold" /> {sv ? 'Beräknad månadskostnad' : 'Estimated Monthly Cost'}
                    </p>
                    <div className="flex items-center gap-4 mb-3">
                      <label className="text-xs" style={{ color: 'var(--text-muted)' }}>{sv ? 'Lektioner/vecka:' : 'Lessons/week:'}</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4].map(n => (
                          <button type="button" key={n} onClick={() => setLessonsPerWeek(n)}
                            className={`w-9 h-9 rounded text-sm font-medium ${lessonsPerWeek === n ? 'bg-gold text-black' : ''}`}
                            style={lessonsPerWeek !== n ? { backgroundColor: 'var(--bg-primary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' } : {}}>
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="text-center p-3 rounded" style={{ backgroundColor: 'var(--bg-primary)' }}>
                      <p className="text-2xl font-bold text-gold">{monthlyCostMin.toLocaleString()} SEK<span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>/{sv ? 'månad' : 'month'}</span></p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{priceMin} x {lessonsPerWeek} x 4</p>
                    </div>
                  </div>
                )}

                {/* Message */}
                <div className="mb-10">
                  <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Övrig information (valfritt)' : 'Additional information (optional)'}</label>
                  <textarea rows={3} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="w-full px-4 py-3 rounded resize-none" data-testid="booking-message" />
                </div>

                {error && <div className="mb-6 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="booking-error">{error}</div>}

                <button type="submit" disabled={submitting} className="btn-gold w-full flex items-center justify-center gap-2 text-lg py-4" data-testid="booking-submit">
                  {submitting ? '...' : (sv ? 'Skicka ansökan' : 'Submit Application')} {!submitting && <ArrowRight size={20} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
